import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Eye,
    CheckCircle,
    XCircle,
    MapPin,
    User,
    Shield,
    Trash2,
    RefreshCw,
    Clock,
    Edit
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import {
    useGetTestimonialsQuery,
    useUpdateTestimonialStatusMutation,
    useDeleteTestimonialMutation
} from '../../store/slices/apiSlice';
import UserAvatar from '../../components/common/UserAvatar';

const AdminTestimonials = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [selectedTestimonial, setSelectedTestimonial] = useState<any>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [testimonialToDelete, setTestimonialToDelete] = useState<any>(null);

    // Fetch testimonials
    const { data, isLoading, error, refetch } = useGetTestimonialsQuery();
    const [updateStatus] = useUpdateTestimonialStatusMutation();
    const [deleteTestimonial] = useDeleteTestimonialMutation();


    // Extract testimonials from API response
    const testimonials = data?.data?.testimonials || [];
    const statistics = data?.data?.statistics || { pending: 0, approved: 1, total: 1 };

    // Filter testimonials based on search and filters
    const filteredTestimonials = testimonials.filter((testimonial: any) => {
        const matchesSearch =
            testimonial.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            testimonial.text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            testimonial.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            testimonial.category?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || testimonial.status === statusFilter;
        const matchesCategory = categoryFilter === 'all' || testimonial.category === categoryFilter;

        return matchesSearch && matchesStatus && matchesCategory;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredTestimonials.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedTestimonials = filteredTestimonials.slice(startIndex, startIndex + itemsPerPage);

    const handleViewTestimonial = (testimonial: any) => {
        setSelectedTestimonial(testimonial);
        setShowViewModal(true);
    };

    const handleStatusUpdate = async (id: number, status: 'pending' | 'approved' | 'rejected') => {
        try {
            await updateStatus({ id, status }).unwrap();
            toast.success(`Testimonial ${status} successfully!`);
            refetch();
        } catch (error) {
            console.error('Failed to update status:', error);
            toast.error('Failed to update testimonial status');
        }
    };


    const handleDeleteTestimonial = (testimonial: any) => {
        setTestimonialToDelete(testimonial);
        setShowDeleteModal(true);
    };

    const confirmDeleteTestimonial = async () => {
        if (!testimonialToDelete) return;

        try {
            await deleteTestimonial(testimonialToDelete.id).unwrap();
            toast.success('Testimonial deleted successfully!');
            refetch();
            setShowDeleteModal(false);
            setTestimonialToDelete(null);
        } catch (error: any) {
            console.error('Failed to delete testimonial:', error);

            if (error?.status === 404) {
                toast.error('Testimonial not found. It may have already been deleted.');
            } else if (error?.status === 401) {
                toast.error('Unauthorized: Please log in again.');
            } else if (error?.status === 403) {
                toast.error('You do not have permission to delete this testimonial.');
            } else {
                toast.error('Failed to delete testimonial. Please try again.');
            }
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'approved': return <CheckCircle className="h-4 w-4" />;
            case 'pending': return <Clock className="h-4 w-4" />;
            case 'rejected': return <XCircle className="h-4 w-4" />;
            default: return <Clock className="h-4 w-4" />;
        }
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center">
                        <XCircle className="h-6 w-6 text-red-600 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-red-800">Error Loading Testimonials</h3>
                            <p className="text-red-600 mt-1">There was an error loading the testimonials. Please try again.</p>
                            <button
                                onClick={() => refetch()}
                                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">

            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Testimonials Management</h1>
                        <p className="text-gray-600">Manage customer testimonials and reviews</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate('/admin/testimonials/create')}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                        >
                            <User className="h-4 w-4 mr-2" />
                            Create Testimonial
                        </button>
                        <button
                            onClick={() => refetch()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Testimonials</p>
                            <p className="text-3xl font-bold text-gray-900">{statistics.total}</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <User className="h-6 w-6 text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Approved</p>
                            <p className="text-3xl font-bold text-green-600">{statistics.approved}</p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-full">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Pending</p>
                            <p className="text-3xl font-bold text-yellow-600">{statistics.pending}</p>
                        </div>
                        <div className="p-3 bg-yellow-100 rounded-full">
                            <Clock className="h-6 w-6 text-yellow-600" />
                        </div>
                    </div>
                </div>

            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search testimonials..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">All Status</option>
                            <option value="approved">Approved</option>
                            <option value="pending">Pending</option>
                            <option value="rejected">Rejected</option>
                        </select>

                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">All Categories</option>
                            <option value="laundry">Laundry</option>
                            <option value="investment">Investment</option>
                            <option value="general">General</option>
                        </select>

                    </div>
                </div>
            </div>

            {/* Testimonials Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">Testimonials ({filteredTestimonials.length})</h2>
                </div>

                {filteredTestimonials.length === 0 ? (
                    <div className="p-12 text-center">
                        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No testimonials found</h3>
                        <p className="text-gray-600">No testimonials match your current filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Testimonial</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedTestimonials.map((testimonial: any) => (
                                    <tr key={testimonial.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    <UserAvatar 
                                                        src={testimonial.image_url || testimonial.profile_image} 
                                                        name={testimonial.name} 
                                                        size="sm" 
                                                    />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{testimonial.name}</div>
                                                    <div className="text-sm text-gray-500 flex items-center">
                                                        <MapPin className="h-3 w-3 mr-1" />
                                                        {testimonial.location}
                                                    </div>
                                                    {testimonial.verified && (
                                                        <div className="text-xs text-green-600 flex items-center">
                                                            <Shield className="h-3 w-3 mr-1" />
                                                            Verified
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900 max-w-xs truncate">{testimonial.text}</div>
                                            <div className="text-sm text-gray-500 mt-1">
                                                Helpful: {testimonial.helpful}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <span className="text-yellow-600 font-medium mr-1">★</span>
                                                <span className="text-sm font-medium text-gray-900">{testimonial.rating}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                {getStatusIcon(testimonial.status)}
                                                <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(testimonial.status)}`}>
                                                    {testimonial.status}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                                {testimonial.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDateUtil(testimonial.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleViewTestimonial(testimonial)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="View testimonial details"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                {testimonial.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleStatusUpdate(testimonial.id, 'approved')}
                                                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 transition-colors"
                                                            title="Approve testimonial"
                                                        >
                                                            <CheckCircle className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleStatusUpdate(testimonial.id, 'rejected')}
                                                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                                            title="Reject testimonial"
                                                        >
                                                            <XCircle className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => navigate(`/admin/testimonials/edit/${testimonial.id}`)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="Edit testimonial"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteTestimonial(testimonial)}
                                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                                    title="Delete testimonial"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Previous
                            </button>

                            <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(4, totalPages) }, (_, i) => {
                                    let pageNum;
                                    let showEllipsis = false;

                                    if (totalPages <= 4) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 2) {
                                        pageNum = i + 1;
                                        showEllipsis = i === 3 && totalPages > 4;
                                    } else if (currentPage >= totalPages - 1) {
                                        pageNum = totalPages - 3 + i;
                                        showEllipsis = i === 0 && totalPages > 4;
                                    } else {
                                        pageNum = currentPage - 1 + i;
                                        showEllipsis = (i === 0 && currentPage > 3) || (i === 3 && currentPage < totalPages - 2);
                                    }

                                    return (
                                        <Fragment key={pageNum}>
                                            {showEllipsis && (
                                                <span className="px-2 text-gray-500">...</span>
                                            )}
                                            <button
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-8 h-8 p-0 text-sm border rounded-md ${currentPage === pageNum
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        </Fragment>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* View Modal */}
            {showViewModal && selectedTestimonial && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Testimonial Details</h3>
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ×
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center space-x-4">
                                <UserAvatar 
                                    src={selectedTestimonial.image_url || selectedTestimonial.profile_image} 
                                    name={selectedTestimonial.name} 
                                    size="lg" 
                                />
                                <div>
                                    <h4 className="text-xl font-semibold text-gray-900">{selectedTestimonial.name}</h4>
                                    <div className="flex items-center text-gray-600">
                                        <MapPin className="h-4 w-4 mr-1" />
                                        {selectedTestimonial.location}
                                    </div>
                                    <div className="flex items-center mt-1">
                                        <span className="text-yellow-600 font-medium mr-1">★</span>
                                        <span className="text-sm font-medium">{selectedTestimonial.rating}/5</span>
                                        {selectedTestimonial.verified && (
                                            <span className="ml-2 text-xs text-green-600 flex items-center">
                                                <Shield className="h-3 w-3 mr-1" />
                                                Verified
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h5 className="font-semibold text-gray-900 mb-2">Testimonial</h5>
                                <p className="text-gray-700 leading-relaxed">{selectedTestimonial.text}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4 border-t pt-4">
                                <div>
                                    <h6 className="font-medium text-gray-900">Status</h6>
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedTestimonial.status)}`}>
                                        {selectedTestimonial.status}
                                    </span>
                                </div>
                                <div>
                                    <h6 className="font-medium text-gray-900">Category</h6>
                                    <span className="text-sm text-gray-600">{selectedTestimonial.category}</span>
                                </div>
                                <div>
                                    <h6 className="font-medium text-gray-900">Helpful Votes</h6>
                                    <span className="text-sm text-gray-600">{selectedTestimonial.helpful}</span>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h6 className="font-medium text-gray-900 mb-2">Submission Details</h6>
                                <div className="text-sm text-gray-600 space-y-1">
                                    <div>Submitted: {formatDateTimeUtil(selectedTestimonial.created_at)}</div>
                                    <div>Last Updated: {formatDateTimeUtil(selectedTestimonial.updated_at)}</div>
                                    <div>IP Address: {selectedTestimonial.ip_address}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && testimonialToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Delete Testimonial</h3>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ×
                            </button>
                        </div>

                        <div className="mb-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                                    <Trash2 className="h-6 w-6 text-red-600" />
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900">{testimonialToDelete.name}</h4>
                                    <p className="text-sm text-gray-600">{testimonialToDelete.location}</p>
                                </div>
                            </div>

                            <p className="text-gray-600 mb-4">
                                Are you sure you want to delete this testimonial? This action cannot be undone.
                            </p>

                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-sm text-gray-700 italic">"{testimonialToDelete.text.substring(0, 100)}..."</p>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteTestimonial}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Testimonial
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTestimonials;
